import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { isSubdomainRoute } from "@/utils/subdomain";
import { useCognitoAuth } from "@/cognito/cognito.config";
import { getCognitoAccessToken } from "@/cognito/auth";
import { useUserStore } from "@/stores/user";
import CognitoCallbackView from "@/views/cognito/CognitoCallbackView.vue";
import ImpersonateView from "@/views/cognito/ImpersonateView.vue";
import SubscriptionInviteCognitoLandingView from "@/views/cognito/SubscriptionInviteCognitoLandingView.vue";
import CompanyEmailInviteCognitoLandingView from "@/views/cognito/CompanyEmailInviteCognitoLandingView.vue";
import CompanyInviteExpertRedirectView from "@/views/cognito/CompanyInviteExpertRedirectView.vue";

// Insights Feature
import InsightsLayout from "../views/insights/InsightsLayout.vue";
import InsightsSearchView from "../views/insights/InsightsSearchView.vue";
import InsightsTrendsView from "../views/insights/InsightsTrendsView.vue";
import InsightsWatchlistView from "../views/insights/InsightsWatchlistView.vue";
import InsightsTranscriptView from "../views/insights/InsightsTranscriptView.vue";

const lazyExpertPrograms = () =>
  import("../views/dashboard/ExpertProgramsView.vue");
const lazyBilling = () => import("../views/dashboard/BillingView.vue");
const lazyBookMeeting = () =>
  import("../views/expert-meeting/BookMeetingView.vue");

const expertProgramRoutes: Array<RouteRecordRaw> = [
  { path: "/company/programs", name: "ExpertPrograms" },
  { path: "/company/programs/advanced-settings", name: "AdvancedSettings" },
  { path: "/company/programs/suggested-experts", name: "SuggestedExperts" },
  { path: "/company/programs/shortlisted-experts", name: "ShortlistedExperts" },
  { path: "/company/programs/experts", name: "Experts" },
  { path: "/company/programs/requests", name: "Requests" },
  { path: "/company/programs/invitations", name: "Invitations" },
].map((route) => ({
  ...route,
  component: lazyExpertPrograms,
  meta: { requiresAuth: true },
}));

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "HomeView",
    component: () => import("../views/HomeView.vue"),
  },
  {
    path: "/oauth/callback",
    component: () => import("../views/CallbackView.vue"),
  },
  // Cognito callback route
  {
    path: "/auth/callback",
    name: "CognitoCallback",
    component: CognitoCallbackView,
  },
  // Admin impersonation — Retool opens this URL with a short-lived JWT
  {
    path: "/auth/impersonate",
    name: "CompanyImpersonate",
    component: ImpersonateView,
  },
  // Cognito: Rails /join/:slug redirects here; stores slug then Hosted UI
  {
    path: "/subscription/invite/:slug",
    name: "SubscriptionInviteCognitoLanding",
    component: SubscriptionInviteCognitoLandingView,
  },
  {
    path: "/company/invite/email/:token",
    name: "CompanyEmailInviteCognitoLanding",
    component: CompanyEmailInviteCognitoLandingView,
  },
  {
    path: "/company/invite/expert-redirect",
    name: "CompanyInviteExpertRedirect",
    component: CompanyInviteExpertRedirectView,
  },
  {
    path: "/company/info",
    name: "AddCompanyDetails",
    component: () => import("../views/AddCompanyDetailsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/signup",
    component: () => import("@/views/frontegg/SignUpView.vue"),
  },
  {
    path: "/invite/company",
    redirect: "/signup",
  },
  {
    path: "/company/analytics",
    name: "AnalyticsPage",
    component: () => import("../views/dashboard/AnalyticsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/company/expert-program/add-program-details",
    name: "AddProgramDetails",
    component: () =>
      import("../views/expert-program/AddProgramDetailsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/company/expert-program/add-account-balance",
    name: "AddAccountBalance",
    component: () =>
      import("../views/expert-program/AddAccountBalanceView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/experts_booking_system",
    name: "ExpertsDirectory",
    component: () => import("../views/expert-program/ExpertsDirectoryView.vue"),
  },
  {
    path: "/company/expert-program/add-integration",
    name: "AddIntegration",
    component: () => import("../views/expert-program/AddIntegrationView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/company/integration/salesforce",
    name: "AddSalesforceIntegration",
    component: () =>
      import("../views/expert-program/AddSalesforceIntegrationView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/company/integration/hubspot",
    name: "AddHubspotIntegration",
    component: () =>
      import("../views/expert-program/AddHubspotIntegrationView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/company/programs/experts/invite",
    name: "InviteExpertsDirect",
    component: () => import("../views/expert-program/InviteExpertsView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/company/billing-page",
    name: "BillingPage",
    component: lazyBilling,
    meta: { requiresAuth: true },
  },
  {
    path: "/company/billing-page/add",
    name: "BillingPageAdd",
    component: lazyBilling,
    meta: { requiresAuth: true },
  },
  {
    path: "/company/account-page",
    name: "AccountPage",
    component: () => import("../views/dashboard/AccountView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/company/team-page",
    name: "TeamPage",
    component: () => import("../views/dashboard/TeamView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/company/subscription-page",
    name: "SubscriptionPage",
    component: () => import("../views/dashboard/SubscriptionView.vue"),
    meta: { requiresAuth: true },
  },
  ...expertProgramRoutes,
  {
    path: "/company/signin/landing",
    name: "SignInLanding",
    component: () => import("../views/SignInLandingView.vue"),
  },
  {
    path: "/company/expert-meeting/book-meeting",
    name: "BookMeeting",
    component: lazyBookMeeting,
  },
  {
    path: "/directory-testing",
    name: "DirectoryTesting",
    component: () => import("../views/DirectoryTestingView.vue"),
  },
  {
    path: "/expert/signup",
    name: "ExpertSignup",
    component: () => import("../views/ExpertSignup.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/company/create-play",
    name: "CreatePlay",
    component: () => import("../views/dashboard/CreatePlayView.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/book-meeting/:programSlug/:expertSlug",
    name: "BookMeetingBySlug",
    component: lazyBookMeeting,
  },
  // ========================================
  // Insights Feature Routes
  // ========================================
  {
    path: "/insights",
    component: InsightsLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "InsightsSearch",
        component: InsightsSearchView,
      },
      {
        path: "search",
        redirect: { name: "InsightsSearch" },
      },
      {
        path: "trends",
        name: "InsightsTrends",
        component: InsightsTrendsView,
      },
      {
        path: "watchlist",
        name: "InsightsWatchlist",
        component: InsightsWatchlistView,
      },
      {
        path: "transcript/:id",
        name: "InsightsTranscript",
        component: InsightsTranscriptView,
      },
    ],
  },
];

const subdomainRoutes: Array<RouteRecordRaw> = [
  {
    path: "/:layoutSlug?",
    name: "BookingSystemSubdomain",
    component: () => import("../views/booking-system/BookingSystemView.vue"),
  },
];

const activeRoutes = isSubdomainRoute() ? subdomainRoutes : routes;

const router = createRouter({
  history: createWebHistory(),
  routes: activeRoutes,
});

/** Cognito: block direct navigation to protected routes without a session (Frontegg keeps its own guard). */
router.beforeEach(async (to, _from, next) => {
  if (!useCognitoAuth) {
    next();
    return;
  }
  const requiresAuth = to.matched.some((record) => record.meta?.requiresAuth);
  if (!requiresAuth) {
    next();
    return;
  }
  const userStore = useUserStore();
  if (userStore.token) {
    next();
    return;
  }
  const access = await getCognitoAccessToken();
  if (access) {
    next({ name: "HomeView", replace: true });
    return;
  }
  next({ name: "HomeView", replace: true });
});

export default router;
